import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components/macro';

import { MANAGE_PLATFORM_PRIVILEGE_MAP } from '../entity/shared/rbacConstantsService';
import { useGetGrantedPrivilegesQuery } from '../../graphql/policy.generated';
import { useGetAuthenticatedUserUrn } from '../useGetAuthenticatedUser';
import { EntityType, PlatformPrivileges } from '../../types.generated';
import { UnauthorisedPage } from '../shared/UnauthorisedPage';
import { useUserContext } from '../context/useUserContext';
import { useEntityRegistry } from '../useEntityRegistry';
import CompactContext from '../shared/CompactContext';
import { decodeUrn } from '../entity/shared/utils';
import analytics from '../analytics/analytics';
import { EventType } from '../analytics';

const EmbeddedPageWrapper = styled.div`
    max-height: 100%;
    overflow: auto;
    padding: 16px;
`;

interface RouteParams {
    urn: string;
}

interface Props {
    entityType: EntityType;
}

export default function EmbeddedPage({ entityType }: Props) {
    const entityRegistry = useEntityRegistry();
    const { platformPrivileges } = useUserContext();
    const { urn: encodedUrn } = useParams<RouteParams>();
    const urn = decodeUrn(encodedUrn);

    useEffect(() => {
        analytics.event({
            type: EventType.EmbedProfileViewEvent,
            entityType,
            entityUrn: urn,
        });
    }, [entityType, urn]);

    const authenticatedUserUrn = useGetAuthenticatedUserUrn();
    const { data } = useGetGrantedPrivilegesQuery({
        variables: {
            input: {
                actorUrn: authenticatedUserUrn,
                resourceSpec: { resourceType: entityType, resourceUrn: urn },
            },
        },
        fetchPolicy: 'cache-first',
    });

    const canViewEntityPage =
        platformPrivileges?.[MANAGE_PLATFORM_PRIVILEGE_MAP[entityType] as keyof PlatformPrivileges];

    return (
        <CompactContext.Provider value>
            {data && !canViewEntityPage && <UnauthorisedPage />}
            {data && canViewEntityPage && (
                <EmbeddedPageWrapper>{entityRegistry.renderEmbeddedProfile(entityType, urn)}</EmbeddedPageWrapper>
            )}
        </CompactContext.Provider>
    );
}
